/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent } from 'react';
import { types, Instance, flow } from 'mobx-state-tree';
import { PaymentModel, IPaymentModel } from './models/payment.model';
import { DateRangeModel, IDateRangeModel } from './models/dateRange.model';
import { Payment } from './types';
import { DateRange } from 'materialui-daterange-picker';
import { AxiosResponse } from 'axios';
import axios from 'server';
import { filterGroupsList } from './filterGropsList';
import { capitalizeFirstLetter, toFormatDate } from 'utils';
import { todayStart, todayEnd } from 'components/DateRangePicker/DefaultRanges';

const { model, array, string } = types;

export const PaymentsTableStore = model('PaymentsTableStore', {
  payments: array(PaymentModel),
  filter: string,
  groupFilter: array(string),
  dateRangeFilter: DateRangeModel,
})
  .views((self) => ({
    get paymentsView(): IPaymentModel[] {
      const paymentsInDateRange = self.payments.filter(
        ({ subscriptionPaymentDate }) =>
          new Date(subscriptionPaymentDate) >= self.dateRangeFilter.startDate &&
          new Date(subscriptionPaymentDate) <= self.dateRangeFilter.endDate
      );

      const groupedPayments = paymentsInDateRange.filter(
        ({ status }) => self.groupFilter.findIndex((group) => group === status) > -1
      );

      if (!self.filter) {
        return groupedPayments;
      }

      const regexp = new RegExp(self.filter, 'i');
      return groupedPayments.filter(
        ({
          amount,
          status,
          email,
          name,
          organizationName,
          subscriptionExpirationDate,
          subscriptionPaymentDate,
          noOfUsers,
        }) =>
          regexp.test(
            `${amount}${status}${email}${name}${organizationName}${subscriptionExpirationDate}${toFormatDate(
              subscriptionExpirationDate
            )}${toFormatDate(subscriptionPaymentDate)}${subscriptionPaymentDate}${noOfUsers}`
          )
      );
    },
    get showAllDateRange(): IDateRangeModel {
      return {
        startDate: self.payments.length
          ? new Date(self.payments[self.payments.length - 1]?.subscriptionPaymentDate)
          : todayStart,
        endDate: self.payments.length ? new Date(self.payments[0]?.subscriptionPaymentDate) : todayEnd,
      };
    },
    get paymentsDateRangeDescription(): string {
      if (self.dateRangeFilter.startDate.toDateString() === self.dateRangeFilter.endDate.toDateString()) {
        return ` (${self.dateRangeFilter.startDate.toDateString()})`;
      }
      return ` (from ${self.dateRangeFilter.startDate.toDateString()} to ${self.dateRangeFilter.endDate.toDateString()})`;
    },
    getRenderValueForGroupSelect(): string {
      const renderValues = self.groupFilter.map((group) => `${capitalizeFirstLetter(group)}`);
      return renderValues.join(', ');
    },
  }))
  .actions((self) => ({
    normalizePayments({ data: { data } }: AxiosResponse<{ data: Payment[] }>): IPaymentModel[] | [] {
      const normalizedPayments: IPaymentModel[] = [];
      if (data.length === 0) {
        return normalizedPayments;
      }
      normalizedPayments.push({
        id: 0,
        amount: '0',
        currency: 'USD',
        status: 'Active',
        email: '',
        name: '',
        organizationName: '',
        noOfUsers: 0,
        subscriptionExpirationDate: '30-day trial',
        subscriptionPaymentDate: data[0].clientData.subscriptionPaymentDate || data[0].createdAt,
      });
      for (let index = 0; index < data.length; index++) {
        normalizedPayments.push({
          id: data[index].id,
          amount: data[index].amount ? parseFloat(data[index].amount).toFixed(2) : data[index].amount,
          currency: data[index].currency,
          status: data[index].status,
          email: data[index].clientData.email,
          name: data[index].clientData.name,
          organizationName: data[index].clientData.organizationName,
          noOfUsers: data[index].noOfUsers,
          subscriptionExpirationDate: data[index].clientData.subscriptionExpirationDate,
          subscriptionPaymentDate: data[index].clientData.subscriptionPaymentDate || data[index].createdAt,
        });
      }
      self.payments.replace(normalizedPayments);

      self.groupFilter.replace(filterGroupsList);

      self.dateRangeFilter = {
        startDate: new Date(self.payments[self.payments.length - 1]?.subscriptionPaymentDate || todayStart),
        endDate: new Date(self.payments[0]?.subscriptionPaymentDate || todayEnd),
      };
      return self.payments;
    },
    setFilter(filter: string) {
      self.filter = filter;
    },
    setGroupFilter(event: ChangeEvent<{ value: unknown }>): void {
      self.groupFilter.replace(event.target.value as string[]);
    },
    setDateRangeFilter({ startDate, endDate }: DateRange): void {
      self.dateRangeFilter.startDate = startDate as Date;
      self.dateRangeFilter.endDate = endDate as Date;
    },
  }))
  .actions((self) => ({
    getPayments: flow(function* () {
      const result = yield axios
        .get('subscription/payments')
        .then(self.normalizePayments)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
  }));

export type IPaymentsTableStore = Instance<typeof PaymentsTableStore>;
